'use client';
import { createContext, memo, type ReactNode, useContext, useEffect, useRef } from 'react';
import invariant from 'tiny-invariant';
import { createStore, type StoreApi, useStore } from 'zustand';
import { persist } from 'zustand/middleware';

import { reportErrorToBugsnag } from '@air/utils-error';

import { createPlayerSlice, type PlayerSlice } from '../store/createPlayerSlice';
import { PlayerSync } from './PlayerSync';

export type PlayerStoreState = PlayerSlice;

export const PlayerContext = createContext<StoreApi<PlayerStoreState> | null>(null);

export type PlayerContextProviderProps = {
  children: ReactNode;
  id: string;
  onTimeUpdate?: (currentTime: number) => void;
  storageKey: string;
};

/**
 * IMPORTANT: This component exists solely to create the Zustand store and reset it when necessary.
 * Do NOT add feature logic, side effects, or business logic to this component.
 * Adding "quick-win" implementations here led to technical debt and the need for refactoring.
 * Use custom hooks or wrapper components for additional functionality instead.
 */

export const PlayerContextProvider = memo(({ children, id, onTimeUpdate, storageKey }: PlayerContextProviderProps) => {
  const storeRef = useRef<StoreApi<PlayerStoreState> | null>(null);

  invariant(storageKey, 'storageKey is required');

  if (!storeRef.current) {
    storeRef.current = createStore<PlayerStoreState>()(
      persist((...args) => ({ ...createPlayerSlice({ id })(...args) }), {
        name: storageKey,
        partialize: (state) => ({
          muted: state.muted,
          playbackRate: state.playbackRate,
          timeFormat: state.timeFormat,
          volume: state.volume,
        }),
        version: 1,
      }),
    );
  }

  useEffect(() => {
    const state = storeRef.current?.getState();

    if (id && state?.initialized && state?.id !== id) {
      state.reset(id);
    }
  }, [id]);

  return (
    <PlayerContext.Provider value={storeRef.current}>
      <PlayerSync key={id} onTimeUpdate={onTimeUpdate} />
      {children}
    </PlayerContext.Provider>
  );
});

PlayerContextProvider.displayName = 'PlayerContextProvider';

export type HTMLVideoElementWithoutEventListeners = Omit<HTMLVideoElement, 'addEventListener' | 'removeEventListener'>;

export function usePlayerStore<T>(
  selector: (store: PlayerStoreState) => T,
  options: { dangerouslyIncludeEventListeners: true },
): T;

export function usePlayerStore<T>(
  selector: (store: PlayerStoreState) => T,
  options?: { dangerouslyIncludeEventListeners?: false },
): T extends HTMLVideoElement ? HTMLVideoElementWithoutEventListeners : T;

export function usePlayerStore<T>(
  selector: (store: PlayerStoreState) => T,
  _options?: { dangerouslyIncludeEventListeners?: boolean },
): T {
  const store = useContext(PlayerContext);

  if (!store) {
    const context = 'usePlayerStore must be used within a PlayerContextProvider';
    const error = new Error(context);

    reportErrorToBugsnag({
      context,
      error,
    });

    throw error;
  }

  return useStore(store, selector);
}
